import { useState, useRef, useEffect, useMemo } from "react";
import { Plus, Search, Tag, Zap, Hash } from "lucide-react";
import { mapApiTag, MappedTag } from "@/utils/tagMapping";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CreateTagDialog } from "@/components/tags/CreateTagDialog";
import { TagList } from "@/components/tags/TagList";
import { TagGrid } from "@/components/tags/TagGrid";
import ViewToggle from "@/components/ViewToggle";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as tagsApi from "@/services/tagsApi";

export default function TagsPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("tagsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "grid";
  });
  const tagListRef = useRef<{ refreshTags: () => void }>(null);
  const queryClient = useQueryClient();

  const { data: apiTags = [], isLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => tagsApi.listTags(),
  });

  const tags: MappedTag[] = useMemo(() => apiTags.map((t: any) => mapApiTag(t)), [apiTags]);

  const counts = useMemo(() => {
    const c = { total: tags.length, static: 0, dynamic: 0 };
    tags.forEach((t) => {
      if (t.type === "dynamic") c.dynamic++;
      else c.static++;
    });
    return c;
  }, [tags]);

  const filteredTags = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return tags.filter((tag) => {
      if (typeFilter && tag.type !== typeFilter) return false;
      if (!q) return true;
      return (
        tag.name.toLowerCase().includes(q) ||
        tag.description?.toLowerCase().includes(q) ||
        tag.type.toLowerCase().includes(q)
      );
    });
  }, [tags, searchQuery, typeFilter]);

  const deleteTag = async (id: string) => {
    try {
      await tagsApi.deleteTagById(id);
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      toast.success("Tag deleted");
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast.error("Failed to delete tag");
    }
  };

  const handleTagCreated = () => {
    setIsCreateDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ["tags"] });
  };

  useEffect(() => {
    localStorage.setItem("tagsViewPreference", view);
  }, [view]);

  const handleTypeFilter = (type: string) => {
    setTypeFilter(typeFilter === type ? null : type);
  };

  return (
    <div className="bg-background w-full">
      <div className="w-full px-6 py-6 space-y-6">
        <PageHeader title="Tags" description="Organize devices with static tags or create dynamic tags with custom criteria">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            <ViewToggle view={view} onViewChange={setView} />
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Tag
            </Button>
          </div>
        </PageHeader>

        {/* Status summary & filter chips */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setTypeFilter(null)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              typeFilter === null
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            <Tag className="h-3.5 w-3.5" />
            All
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{counts.total}</Badge>
          </button>
          <button
            onClick={() => handleTypeFilter("static")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              typeFilter === "static"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            <Hash className="h-3.5 w-3.5" />
            Static
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{counts.static}</Badge>
          </button>
          <button
            onClick={() => handleTypeFilter("dynamic")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              typeFilter === "dynamic"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/50 text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            <Zap className="h-3.5 w-3.5" />
            Dynamic
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">{counts.dynamic}</Badge>
          </button>

          {(searchQuery || typeFilter) && (
            <span className="text-sm text-muted-foreground ml-2">
              Showing {filteredTags.length} of {counts.total} tags
            </span>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {view === "list" ? (
              <TagList ref={tagListRef} searchQuery={searchQuery} typeFilter={typeFilter} />
            ) : (
              <TagGrid tags={filteredTags} onDelete={deleteTag} />
            )}
          </>
        )}

        <CreateTagDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onTagCreated={handleTagCreated}
        />
      </div>
    </div>
  );
}
